import isElectron from '../utils/isElectron';

export type ReleaseChannel = 'dev' | 'next' | 'lifetime' | 'production' | 'web';

const DESKTOP_CHANNELS: ReleaseChannel[] = [
  'dev',
  'next',
  'lifetime',
  'production',
];

export interface ArchivedVersion {
  version: string;
  channel: string;
  releaseDate: string;
  prerelease: boolean;
  files: Record<string, string>;
}

export interface VersionArchive {
  channel: string;
  productName: string;
  baseUrl: string;
  archiveLimit: number;
  updated: string;
  versions: ArchivedVersion[];
}

export interface ReleaseChannelState {
  channel: ReleaseChannel;
  pinnedVersion: string | null;
  switchableChannels: ReleaseChannel[];
  currentVersion: string;
}

/**
 * Which update feed this install follows. On desktop the choice lives in
 * userData and is seeded from app.json, so a member who opts into Lifetime
 * keeps that feed across updates. The browser build is always 'web'.
 *
 * Resolved once and cached so callers can read it synchronously afterwards.
 */
class ReleaseChannelService {
  private static channel: ReleaseChannel | null = null;
  private static pending: Promise<ReleaseChannel> | null = null;

  static async resolve(): Promise<ReleaseChannel> {
    if (this.channel) {
      return this.channel;
    }
    if (this.pending) {
      return this.pending;
    }

    this.pending = this.read();
    this.channel = await this.pending;
    this.pending = null;

    return this.channel;
  }

  /**
   * Last resolved channel, or null if resolve() has not finished yet.
   */
  static current(): ReleaseChannel | null {
    return this.channel;
  }

  static isDev(): boolean {
    return this.channel === 'dev';
  }

  private static async read(): Promise<ReleaseChannel> {
    if (!isElectron() || !window.api?.getReleaseChannel) {
      return 'web';
    }

    try {
      const value = await window.api.getReleaseChannel();
      if (DESKTOP_CHANNELS.includes(value as ReleaseChannel)) {
        return value as ReleaseChannel;
      }
      return 'production';
    } catch (error) {
      console.error('Failed to resolve release channel:', error);
      return 'production';
    }
  }

  static async getState(): Promise<ReleaseChannelState | null> {
    if (!isElectron() || !window.api?.releaseChannel) {
      return null;
    }

    try {
      const state = await window.api.releaseChannel.getState();
      return {
        channel: state.channel as ReleaseChannel,
        pinnedVersion: state.pinnedVersion,
        switchableChannels: state.switchableChannels as ReleaseChannel[],
        currentVersion: state.currentVersion,
      };
    } catch (error) {
      console.error('Failed to read release channel state:', error);
      return null;
    }
  }

  static async setChannel(channel: ReleaseChannel): Promise<void> {
    if (!isElectron() || !window.api?.releaseChannel) {
      return;
    }

    await window.api.releaseChannel.set(channel);
    // The cached value is now stale, and the next resolve() should re-read it.
    this.channel = channel;
  }

  static async getArchive(): Promise<VersionArchive | null> {
    if (!isElectron() || !window.api?.releaseChannel) {
      return null;
    }

    try {
      return await window.api.releaseChannel.getArchive();
    } catch (error) {
      console.error('Failed to read version archive:', error);
      return null;
    }
  }

  /**
   * Pins the updater to one archived release and starts the switch. Passing
   * null clears the pin and returns to the channel's latest build.
   */
  static async installVersion(
    version: string | null,
  ): Promise<{ ok: boolean; message?: string }> {
    if (!isElectron() || !window.api?.releaseChannel) {
      return { ok: false, message: 'Only available in the desktop app' };
    }

    return window.api.releaseChannel.installVersion(version);
  }
}

export default ReleaseChannelService;
